import { useState, useEffect } from "react";

// Third Party
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Formik, Field } from "formik";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

// Services and Type
import type { MutateDepartmentType } from "@/types/soe";
import { createCompanyDepartment } from "@/services/soe";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/department/add/",
)({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			data: search.data as string | undefined,
		};
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const { data } = Route.useSearch();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const id = localStorage.getItem("companyId") || "";
	const [parseData, setParseData] = useState({} as MutateDepartmentType);
	const companyId = id.replace(/^"(.+)"$/, "$1"); // Replace with your actual company ID

	// Effects
	useEffect(() => {
		if (data) {
			setParseData(JSON.parse(data));
		}
	}, [data]);

	// Mutation
	const createDepartmentMutation = useMutation({
		mutationFn: (data: MutateDepartmentType) => createCompanyDepartment(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["departments", companyId] });
			toast({
				title: "ສຳເລັດ",
				description: "ສ້າງຂ້ໍມູນພະແນກສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	// Functions
	const handleOnSubmit = (values: MutateDepartmentType) => {
		createDepartmentMutation.mutate(values);
	};

	// Data
	const mutationPending = createDepartmentMutation.isPending;

	return (
		<div className="space-y-[24px]">
			<div className="text-[18px]">
				<h2 className="font-semibold text-zinc-950">ຂ້ໍມູນການຈັດຕັ້ງພະແນກ</h2>
				<p className="text-zinc-500">
					ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນການຈັດຕັ້ງພະແນກຄະນະອຳນວຍການຂອງລັດວິສາຫະກິດ
				</p>

				<Separator className="my-6" />
			</div>

			<Formik
				enableReinitialize
				initialValues={{
					companyId: companyId,
					deptName: parseData?.deptName || "",
					headCount: parseData?.headCount || 0,
					femaleHeadCount: parseData?.femaleHeadCount || 0,
					empCount: parseData?.empCount || 0,
					femaleEmpCount: parseData?.femaleEmpCount || 0,
				}}
				onSubmit={(values: MutateDepartmentType) => {
					console.log(values);

					handleOnSubmit(values);

					// setOpenModal(false);
				}}
			>
				{({ handleSubmit }) => (
					<form onSubmit={handleSubmit} className="space-y-[16px]">
						<div>
							<Label htmlFor="deptName">
								ຊື່ພະແນກການ/ຝ່າຍ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="deptName"
								placeholder="ປ້ອນຊື່ພະແນກການ/ຝ່າຍ..."
							/>
						</div>

						<div>
							<Label htmlFor="headCount">
								ຄະນະພະແນກ <span className="text-red-500">*</span>
							</Label>
							<Field as={Input} required name="headCount" type="number" />
						</div>

						<div>
							<Label htmlFor="femaleHeadCount">
								ຄະນະພະແນກ (ຍິງ) <span className="text-red-500">*</span>
							</Label>
							<Field as={Input} required name="femaleHeadCount" type="number" />
						</div>
						<div>
							<Label htmlFor="empCount">
								ຈຳນວນຄະນະພະນັກງານ (ທັງໝົດ) <span className="text-red-500">*</span>
							</Label>
							<Field as={Input} required name="empCount" type="number" />
						</div>
						<div>
							<Label htmlFor="femaleEmpCount">
								ຈຳນວນຄະນະພະນັກງານ (ຍິງ) <span className="text-red-500">*</span>
							</Label>
							<Field as={Input} required name="femaleEmpCount" type="number" />
						</div>

						<div className="flex items-center justify-center w-full gap-4">
							<Button
								type="button"
								variant={"outline"}
								onClick={() =>
									navigate({
										to: "..",
										replace: true,
									})
								}
							>
								ຍົກເລີກ
							</Button>
							<Button disabled={mutationPending} type="submit">
								{mutationPending ? "ກຳລັງດຳເນີນການ..." : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</div>
	);
}
